import { Component, OnInit } from '@angular/core';
import { DialogService } from 'src/app/services/dialog.service';
import { FormStudioInformationComponent } from '../form-studio-information/form-studio-information.component';
import { AmiReportingComponent } from '../ami-reporting/ami-reporting.component';
import { FormEnrollmentPoliciesComponent } from '../form-enrollment-policies/form-enrollment-policies.component';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { LoggerService } from '../../../services/logger.service';

type OperationStatusKey = 'workingHours' | 'sessionStartTimes' | 'amiReporting';

interface OperationStatus {
  key: OperationStatusKey;
  label: string;
  configured: boolean;
  loading: boolean;
}

@Component({
  selector: 'app-studio-information',
  templateUrl: './studio-information.component.html',
  styleUrls: ['./studio-information.component.css']
})
export class StudioInformationComponent implements OnInit {

  operationStatuses: OperationStatus[] = [
    { key: 'workingHours', label: 'Working hours defined', configured: false, loading: true },
    { key: 'sessionStartTimes', label: 'Session start times configured', configured: false, loading: true },
    { key: 'amiReporting', label: 'AMI reporting enabled', configured: false, loading: true }
  ];

  constructor(
    private dialogService: DialogService,
    private drupalRESTService: DrupalRESTService,
    private permissionsService: PermissionsService
  ) { }

  ngOnInit(): void {
    this.refreshOperationStatuses();
  }

  openStudioInformationDialog(): void {
    this.dialogService.openDialog(
      FormStudioInformationComponent,
      'responsiveWithData',
      { action: 'edit' }
    ).afterClosed().subscribe(result => {
      if (result) {
        LoggerService.log('Studio information updated');
      }
    });
  }

  openAmiReportingDialog(): void {
    this.dialogService.openDialog(
      AmiReportingComponent,
      'responsiveWithData',
      { action: 'edit' }
    ).afterClosed().subscribe(this.handleDialogResult.bind(this));
  }

  openEnrollmentPoliciesDialog(): void {
    this.dialogService.openDialog(
      FormEnrollmentPoliciesComponent,
      'responsiveWithData',
      { action: 'edit' }
    ).afterClosed().subscribe(this.handleDialogResult.bind(this));
  }

  private handleDialogResult(result: boolean) {
    if (result) {
      this.refreshOperationStatuses();
    }
  }

  private refreshOperationStatuses(): void {
    this.loadWorkingHoursStatus();
    this.loadSessionStartTimesStatus();
    this.loadAmiReportingStatus();
  }

  private loadWorkingHoursStatus(): void {
    this.setOperationLoading('workingHours');
    this.drupalRESTService.httpGET('/api_rest/v1/loadStudioConfig?config_field_name=field_day_view_reference')
      .subscribe({
        next: response => {
          const workingHours = response?.['field_studio_working_hours'];
          const configured = Array.isArray(workingHours) && workingHours.some(entry => this.isWorkingHourActive(entry));
          this.setOperationStatus('workingHours', configured);
        },
        error: () => this.setOperationStatus('workingHours', false)
      });
  }

  private loadSessionStartTimesStatus(): void {
    this.setOperationLoading('sessionStartTimes');
    this.drupalRESTService.httpGET('/api_rest/v1/loadStudioConfig?config_field_name=field_session_start_times_ref')
      .subscribe({
        next: response => {
          const days = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
          const configured = days.some(day => {
            const value = response?.[`field_${day}`]?.[0]?.value;
            return typeof value === 'string' && value.trim().length > 0;
          });
          this.setOperationStatus('sessionStartTimes', configured);
        },
        error: () => this.setOperationStatus('sessionStartTimes', false)
      });
  }

  private loadAmiReportingStatus(): void {
    this.setOperationLoading('amiReporting');
    this.drupalRESTService.httpGET('/api_rest/v1/loadStudioConfig?config_field_name=field_ami_reporting')
      .subscribe({
        next: response => {
          const requiredFields = ['field_ami_address', 'field_client_id', 'field_user_name', 'field_password'];
          const configured = requiredFields.every(field => {
            const value = response?.[field]?.[0]?.value;
            return typeof value === 'string' && value.trim().length > 0;
          });
          this.setOperationStatus('amiReporting', configured);
        },
        error: () => this.setOperationStatus('amiReporting', false)
      });
  }

  private isWorkingHourActive(entry: any): boolean {
    if (!entry) {
      return false;
    }

    const start = this.normalizeHourValue(entry?.starthours);
    const end = this.normalizeHourValue(entry?.endhours);
    const closed = start === 'closed';
    const allDay = entry?.all_day === true || entry?.all_day === '1';

    if (closed) {
      return false;
    }

    if (allDay) {
      return true;
    }

    return start.length === 4 && end.length === 4 && start !== end;
  }

  private normalizeHourValue(value: any): string {
    if (value == null) {
      return '';
    }
    const normalized = value.toString().toLowerCase();
    if (normalized === 'closed') {
      return 'closed';
    }
    return normalized.padStart(4, '0');
  }

  private setOperationLoading(key: OperationStatusKey) {
    const status = this.operationStatuses.find(item => item.key === key);
    if (status) {
      status.loading = true;
    }
  }

  private setOperationStatus(key: OperationStatusKey, configured: boolean) {
    const status = this.operationStatuses.find(item => item.key === key);
    if (status) {
      status.configured = configured;
      status.loading = false;
    }
  }

  get areOperationsConfigured(): boolean {
    return this.operationStatuses.every(status => !status.loading && status.configured);
  }

  get isOverviewLoading(): boolean {
    return this.operationStatuses.some(status => status.loading);
  }

  get canConfigureReporting(): boolean {
    return this.permissionsService.has_access('view_admin_links');
  }
}
